import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  Calculator,
  Users,
  Target,
  Euro,
  TrendingUp,
  Plus,
  RefreshCw,
  Loader2,
  FileText,
  BarChart3,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Send,
  Copy,
  Sparkles,
  Package,
  Tractor,
  Map,
  Calendar,
  Wallet,
  Check,
  Grape,
  Leaf,
  Apple,
  Wheat,
  Flower2,
  Factory,
  Rabbit,
  Beef,
  Carrot,
  Building2,
  UserCheck,
  Gift,
  Percent,
  CreditCard,
  Zap,
  MapPin,
  Clock,
  Navigation,
  Receipt,
  ShoppingCart,
  Bot,
  Plug,
  BookOpen,
  Award,
  Crown,
  Star
} from 'lucide-react';

// ID de la société autorisée
const SOCIETE_FRED_DEV = "d3db4e3f-c6ac-471d-97a8-e58fd382f0cc";

// Vérifier si la société est autorisée (par ID ou par nom)
const isSocieteAutorisee = (societe) => {
  if (!societe) return false;
  if (societe.id === SOCIETE_FRED_DEV) return true;
  if (societe.nom?.toLowerCase().includes("développement")) return true;
  if (societe.nom?.toLowerCase().includes("developpement")) return true;
  return false;
};

// Icônes pour les packs métiers
const PACK_ICONS = {
  viticulture: Grape,
  elevage_bovin: Beef,
  elevage_ovin: Rabbit,
  maraichage: Carrot,
  arboriculture: Apple,
  grandes_cultures: Wheat,
  ppam: Flower2,
  transformation: Factory,
  eta: Tractor,
  equitation: Rabbit,
  bio: Leaf
};

const ConfigurateurProspectPage = () => {
  const { activeSociete } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [activeTab, setActiveTab] = useState('configurateur');
  
  // Données de référence
  const [grilleTarifaire, setGrilleTarifaire] = useState(null);
  const [packsMetiers, setPacksMetiers] = useState({});
  const [commerciaux, setCommerciaux] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [parrainages, setParrainages] = useState([]);
  const [statsVentes, setStatsVentes] = useState(null);
  
  // Configuration prospect
  const [config, setConfig] = useState({
    nom_entreprise: '',
    contact_nom: '',
    contact_prenom: '',
    contact_email: '',
    contact_telephone: '',
    ville: '',
    activites: [],
    surface_ha: 50,
    nb_workspaces: 1,
    nb_collaborateurs: 1,
    options: [],
    demande_speciale: '',
    code_parrainage: '',
    type_paiement: 'mensuel',
    commercial_id: ''
  });
  
  // Devis calculé
  const [devis, setDevis] = useState(null);
  const [parrainageValide, setParrainageValide] = useState(false);
  const [suggestionsIA, setSuggestionsIA] = useState(null);
  
  // Prix ajusté par le commercial
  const [prixAjuste, setPrixAjuste] = useState(null);
  const [remiseCommerciale, setRemiseCommerciale] = useState(0);
  const [notesCommercial, setNotesCommercial] = useState('');
  
  // Dialogs
  const [showPackDetail, setShowPackDetail] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Check access - uniquement Frédéric Développement
  const hasAccess = activeSociete?.id === SOCIETE_FRED_DEV || 
                    activeSociete?.nom === "Frédéric Développement";

  const loadData = useCallback(async () => {
    if (!hasAccess) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [grilleRes, prospectsRes, parrainagesRes, commerciauxRes, statsRes] = await Promise.all([
        api.get('/crm-vente-logiciel/grille-tarifaire').catch(() => ({ data: null })),
        api.get('/crm-vente-logiciel/prospects').catch(() => ({ data: { prospects: [] } })),
        api.get('/crm-vente-logiciel/parrainages').catch(() => ({ data: { parrainages: [] } })),
        api.get('/crm-vente-logiciel/commerciaux').catch(() => ({ data: { commerciaux: [] } })),
        api.get('/crm-vente-logiciel/stats/dashboard').catch(() => ({ data: null }))
      ]);
      
      if (grilleRes.data) {
        setGrilleTarifaire(grilleRes.data.grille);
        setPacksMetiers(grilleRes.data.packs_metiers || {});
      }
      setProspects(prospectsRes.data?.prospects || []);
      setParrainages(parrainagesRes.data?.parrainages || []);
      setCommerciaux(commerciauxRes.data?.commerciaux || []);
      setStatsVentes(statsRes.data);
      
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  }, [hasAccess]);

  useEffect(() => {
    loadData();
  }, [loadData, activeSociete]);

  // Calculer le devis à chaque modification
  const calculerDevis = async () => {
    if (!config.nom_entreprise || !config.contact_email) {
      return;
    }
    
    setCalculating(true);
    try {
      const response = await api.post('/crm-vente-logiciel/calculer-devis', config);
      setDevis(response.data.devis);
      setParrainageValide(response.data.parrainage_valide);
      setSuggestionsIA(response.data.suggestions_ia);
      setPrixAjuste(response.data.devis.total_mensuel);
    } catch (error) {
      console.error('Erreur calcul:', error);
      toast.error('Erreur lors du calcul du devis');
    } finally {
      setCalculating(false);
    }
  };

  // Créer le prospect
  const creerProspect = async () => {
    try {
      const response = await api.post('/crm-vente-logiciel/creer-prospect', config);
      toast.success('Prospect créé avec succès !');
      setShowConfirmation(true);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  // Valider le prix
  const validerPrix = async (prospectId) => {
    try {
      await api.put(`/crm-vente-logiciel/prospects/${prospectId}/valider-prix`, null, {
        params: {
          prix_mensuel: prixAjuste,
          remise_commerciale: remiseCommerciale,
          notes: notesCommercial
        }
      });
      toast.success('Prix validé !');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  // Toggle activité
  const toggleActivite = (packId) => {
    setConfig(c => ({
      ...c,
      activites: c.activites.includes(packId)
        ? c.activites.filter(a => a !== packId)
        : [...c.activites, packId]
    }));
  };

  // Toggle option
  const toggleOption = (optId) => {
    setConfig(c => ({
      ...c,
      options: c.options.includes(optId)
        ? c.options.filter(o => o !== optId)
        : [...c.options, optId]
    }));
  };

  // Access denied
  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center p-6">
        <div className="p-6 rounded-full bg-red-100 mb-6">
          <XCircle className="w-16 h-16 text-red-600" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Accès Restreint</h1>
        <p className="text-muted-foreground max-w-md">
          Ce module est réservé à <strong>Frédéric Développement</strong>
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="configurateur-prospect-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calculator className="w-7 h-7 text-green-600" />
            Configurateur de Prospect
          </h1>
          <p className="text-muted-foreground">
            Calculez le prix idéal basé sur les besoins du client
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadData} variant="ghost" size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats rapides */}
      {statsVentes && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-blue-100">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statsVentes.prospects?.total || 0}</p>
                  <p className="text-sm text-muted-foreground">Prospects</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-green-100">
                  <Euro className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statsVentes.ventes?.mrr || 0}€</p>
                  <p className="text-sm text-muted-foreground">MRR</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-purple-100">
                  <Gift className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statsVentes.parrainages?.codes_actifs || 0}</p>
                  <p className="text-sm text-muted-foreground">Codes parrainage</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-amber-100">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statsVentes.ventes?.arr || 0}€</p>
                  <p className="text-sm text-muted-foreground">ARR</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="configurateur" className="gap-2">
            <Calculator className="w-4 h-4" />
            Configurateur
          </TabsTrigger>
          <TabsTrigger value="prospects" className="gap-2">
            <Target className="w-4 h-4" />
            Prospects ({prospects.length})
          </TabsTrigger>
          <TabsTrigger value="parrainages" className="gap-2">
            <Gift className="w-4 h-4" />
            Parrainages ({parrainages.length})
          </TabsTrigger>
          <TabsTrigger value="commerciaux" className="gap-2">
            <Users className="w-4 h-4" />
            Commerciaux
          </TabsTrigger>
        </TabsList>

        {/* TAB: Configurateur */}
        <TabsContent value="configurateur" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Colonne gauche: Formulaire */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Section 1: Identité */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="w-5 h-5 text-green-600" />
                    1. Identité du prospect
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nom de l'entreprise *</Label>
                      <Input
                        value={config.nom_entreprise}
                        onChange={(e) => setConfig(c => ({ ...c, nom_entreprise: e.target.value }))}
                        placeholder="EARL Les Vignes du Soleil"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ville</Label>
                      <Input
                        value={config.ville}
                        onChange={(e) => setConfig(c => ({ ...c, ville: e.target.value }))}
                        placeholder="Avignon"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prénom du contact *</Label>
                      <Input
                        value={config.contact_prenom}
                        onChange={(e) => setConfig(c => ({ ...c, contact_prenom: e.target.value }))}
                        placeholder="Jean"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nom du contact *</Label>
                      <Input
                        value={config.contact_nom}
                        onChange={(e) => setConfig(c => ({ ...c, contact_nom: e.target.value }))}
                        placeholder="Dupont"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={config.contact_email}
                        onChange={(e) => setConfig(c => ({ ...c, contact_email: e.target.value }))}
                        placeholder="jean.dupont@exploitation.fr"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Téléphone</Label>
                      <Input
                        value={config.contact_telephone}
                        onChange={(e) => setConfig(c => ({ ...c, contact_telephone: e.target.value }))}
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Section 2: Activités / Packs métiers */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Package className="w-5 h-5 text-green-600" />
                    2. Activités agricoles
                  </CardTitle>
                  <CardDescription>
                    Sélectionnez les métiers du prospect - chaque pack déploie automatiquement les modules adaptés
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(packsMetiers).map(([packId, pack]) => {
                      const Icon = PACK_ICONS[packId] || Package;
                      const isSelected = config.activites.includes(packId);
                      
                      return (
                        <div
                          key={packId}
                          onClick={() => toggleActivite(packId)}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-green-500 bg-green-50 shadow-md' 
                              : 'border-border hover:border-green-200 hover:bg-green-50/50'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div 
                              className="p-2 rounded-lg"
                              style={{ backgroundColor: pack.couleur + '20' }}
                            >
                              <Icon 
                                className="w-5 h-5" 
                                style={{ color: pack.couleur }}
                              />
                            </div>
                            {isSelected && (
                              <CheckCircle2 className="w-5 h-5 text-green-600" />
                            )}
                          </div>
                          <h4 className="font-semibold text-sm">{pack.nom}</h4>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {pack.description}
                          </p>
                          <div className="mt-2 flex items-center justify-between">
                            <Badge 
                              variant="outline" 
                              className="text-xs"
                              style={{ borderColor: pack.couleur, color: pack.couleur }}
                            >
                              +{pack.prix_supplementaire}€/mois
                            </Badge>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 px-2 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowPackDetail(pack);
                              }}
                            >
                              Détails
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Section 3: Dimensionnement */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BarChart3 className="w-5 h-5 text-green-600" />
                    3. Dimensionnement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Surface exploitée</Label>
                        <span className="font-bold text-green-600">{config.surface_ha} ha</span>
                      </div>
                      <Slider
                        value={[config.surface_ha]}
                        onValueChange={([v]) => setConfig(c => ({ ...c, surface_ha: v }))}
                        min={0}
                        max={1000}
                        step={10}
                        className="py-2"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>0 ha</span>
                        <span>250 ha</span>
                        <span>500 ha</span>
                        <span>1000 ha</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Nombre de workspaces/sociétés</Label>
                        <div className="flex items-center gap-3">
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => setConfig(c => ({ ...c, nb_workspaces: Math.max(1, c.nb_workspaces - 1) }))}
                          >
                            -
                          </Button>
                          <span className="text-2xl font-bold w-12 text-center">{config.nb_workspaces}</span>
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => setConfig(c => ({ ...c, nb_workspaces: c.nb_workspaces + 1 }))}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Nombre de collaborateurs</Label>
                        <div className="flex items-center gap-3">
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => setConfig(c => ({ ...c, nb_collaborateurs: Math.max(1, c.nb_collaborateurs - 1) }))}
                          >
                            -
                          </Button>
                          <span className="text-2xl font-bold w-12 text-center">{config.nb_collaborateurs}</span>
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => setConfig(c => ({ ...c, nb_collaborateurs: c.nb_collaborateurs + 1 }))}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Section 4: Options avancées */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Zap className="w-5 h-5 text-green-600" />
                    4. Options avancées
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {grilleTarifaire?.options && Object.entries(grilleTarifaire.options).map(([optId, opt]) => {
                      const isSelected = config.options.includes(optId);
                      
                      const icons = {
                        gps_materiel: Navigation,
                        gps_salarie: MapPin,
                        facturation_electronique: Receipt,
                        transformation: Factory,
                        vente_directe: ShoppingCart,
                        agents_ia: Bot,
                        integrations: Plug,
                        comptabilite_avancee: BookOpen,
                        multi_canaux_vente: TrendingUp
                      };
                      const Icon = icons[optId] || Zap;
                      
                      return (
                        <div
                          key={optId}
                          onClick={() => toggleOption(optId)}
                          className={`p-3 border rounded-lg cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-purple-500 bg-purple-50' 
                              : 'border-border hover:border-purple-200'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded ${isSelected ? 'bg-purple-100' : 'bg-muted'}`}>
                              <Icon className={`w-4 h-4 ${isSelected ? 'text-purple-600' : 'text-muted-foreground'}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm">{opt.nom}</span>
                                {isSelected && <Check className="w-4 h-4 text-purple-600" />}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">{opt.description}</p>
                              <Badge variant="outline" className="mt-2 text-xs">
                                +{opt.prix}€/mois
                              </Badge>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Section 5: Demande spéciale IA */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    5. Demande spéciale (analyse IA)
                  </CardTitle>
                  <CardDescription>
                    Décrivez les besoins particuliers du prospect - l'IA suggérera des options adaptées
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={config.demande_speciale}
                    onChange={(e) => setConfig(c => ({ ...c, demande_speciale: e.target.value }))}
                    placeholder="Ex: Le client a une activité de vente directe à la ferme avec 3 marchés par semaine, il souhaite aussi gérer sa comptabilité en interne et exporter vers son cabinet..."
                    rows={4}
                  />
                  
                  {suggestionsIA?.suggestions?.length > 0 && (
                    <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        <span className="font-medium text-purple-800">Suggestions IA</span>
                      </div>
                      <p className="text-sm text-purple-700">{suggestionsIA.explication}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {suggestionsIA.suggestions.map((s, i) => (
                          <Badge key={i} className="bg-purple-100 text-purple-800">
                            {s}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Section 6: Parrainage & Engagement */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Gift className="w-5 h-5 text-green-600" />
                    6. Parrainage & Engagement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Code parrainage (optionnel)</Label>
                      <div className="flex gap-2">
                        <Input
                          value={config.code_parrainage}
                          onChange={(e) => setConfig(c => ({ ...c, code_parrainage: e.target.value.toUpperCase() }))}
                          placeholder="DUPONT-A7X"
                        />
                        {parrainageValide && (
                          <Badge className="bg-green-100 text-green-800 whitespace-nowrap">
                            <Check className="w-3 h-3 mr-1" />
                            -10% / 3 mois
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Type de paiement</Label>
                      <Select 
                        value={config.type_paiement} 
                        onValueChange={(v) => setConfig(c => ({ ...c, type_paiement: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mensuel">Mensuel</SelectItem>
                          <SelectItem value="annuel">Annuel (-15%)</SelectItem>
                          <SelectItem value="2ans">Engagement 2 ans (-20%)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Commercial attribué</Label>
                    <Select 
                      value={config.commercial_id} 
                      onValueChange={(v) => setConfig(c => ({ ...c, commercial_id: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un commercial" />
                      </SelectTrigger>
                      <SelectContent>
                        {commerciaux.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.prenom} {c.nom} ({c.nb_ventes} ventes)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Colonne droite: Devis */}
            <div className="space-y-4">
              <Card className="sticky top-6 border-2 border-green-200 bg-gradient-to-b from-green-50 to-white">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-green-600" />
                    Devis estimé
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button 
                    onClick={calculerDevis} 
                    className="w-full"
                    disabled={calculating || !config.nom_entreprise || !config.contact_email}
                  >
                    {calculating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Calculator className="w-4 h-4 mr-2" />
                    )}
                    Calculer le devis
                  </Button>
                  
                  {devis && (
                    <>
                      <Separator />
                      
                      <div className="space-y-2 text-sm">
                        {devis.details.map((d, i) => (
                          <div key={i} className="flex justify-between">
                            <span className={d.prix < 0 ? 'text-green-600' : ''}>{d.libelle}</span>
                            <span className={`font-medium ${d.prix < 0 ? 'text-green-600' : ''}`}>
                              {d.prix < 0 ? '' : '+'}{d.prix.toFixed(0)}€
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      <Separator />
                      
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold">Total mensuel</span>
                          <span className="text-2xl font-bold text-green-600">
                            {devis.total_mensuel.toFixed(0)}€
                          </span>
                        </div>
                        
                        <div className="flex justify-between text-muted-foreground">
                          <span>Total annuel</span>
                          <span className="font-medium">{devis.total_annuel.toFixed(0)}€</span>
                        </div>
                      </div>
                      
                      <Separator />
                      
                      {/* Ajustement commercial */}
                      <div className="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2 text-amber-800">
                          <Crown className="w-4 h-4" />
                          <span className="font-medium text-sm">Ajustement commercial</span>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-xs">Prix final mensuel</Label>
                          <Input
                            type="number"
                            value={prixAjuste || devis.total_mensuel}
                            onChange={(e) => setPrixAjuste(parseFloat(e.target.value) || 0)}
                            className="text-lg font-bold"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-xs">Remise commerciale (€)</Label>
                          <Input
                            type="number"
                            value={remiseCommerciale}
                            onChange={(e) => setRemiseCommerciale(parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-xs">Notes</Label>
                          <Textarea
                            value={notesCommercial}
                            onChange={(e) => setNotesCommercial(e.target.value)}
                            placeholder="Justification de la remise..."
                            rows={2}
                          />
                        </div>
                      </div>
                      
                      <Button 
                        onClick={creerProspect} 
                        className="w-full bg-green-600 hover:bg-green-700"
                        disabled={!config.nom_entreprise || !config.contact_email}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Créer le prospect
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* TAB: Prospects */}
        <TabsContent value="prospects" className="space-y-4">
          <ScrollArea className="h-[600px]">
            <div className="space-y-3">
              {prospects.map(prospect => (
                <Card key={prospect.id} className="hover:border-green-200 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{prospect.nom_entreprise}</h3>
                          <Badge className={
                            prospect.statut === 'gagne' ? 'bg-green-100 text-green-800' :
                            prospect.statut === 'prix_valide' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100'
                          }>
                            {prospect.statut}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Contact</p>
                            <p>{prospect.contact_prenom} {prospect.contact_nom}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Surface</p>
                            <p>{prospect.surface_ha} ha</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Prix proposé</p>
                            <p className="font-semibold text-green-600">
                              {prospect.prix_mensuel_valide || prospect.prix_mensuel_propose}€/mois
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Commercial</p>
                            <p>{prospect.commercial_nom || '-'}</p>
                          </div>
                        </div>
                        
                        {prospect.activites?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {prospect.activites.map(a => (
                              <Badge key={a} variant="outline" className="text-xs">
                                {packsMetiers[a]?.nom || a}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        {prospect.statut === 'nouveau' && (
                          <Button 
                            size="sm"
                            onClick={() => validerPrix(prospect.id)}
                          >
                            Valider prix
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {prospects.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun prospect configuré</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* TAB: Parrainages */}
        <TabsContent value="parrainages" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {parrainages.map(p => (
              <Card key={p.id}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-purple-100">
                        <Gift className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-semibold">{p.parrain_nom}</p>
                        <p className="text-xs text-muted-foreground">{p.parrain_email}</p>
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-800">Actif</Badge>
                  </div>
                  
                  <div className="bg-gray-50 p-3 rounded-lg flex items-center justify-between">
                    <code className="font-bold text-lg">{p.code}</code>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => {
                        navigator.clipboard.writeText(p.code);
                        toast.success('Code copié !');
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="mt-4 flex justify-between text-sm">
                    <span className="text-muted-foreground">Filleuls</span>
                    <span className="font-semibold">{p.nb_utilisations || 0}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {parrainages.length === 0 && (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                <Gift className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucun code de parrainage créé</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* TAB: Commerciaux */}
        <TabsContent value="commerciaux" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {commerciaux.map(c => (
              <Card key={c.id}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold text-lg">
                      {c.prenom?.[0]}{c.nom?.[0]}
                    </div>
                    <div>
                      <p className="font-semibold">{c.prenom} {c.nom}</p>
                      <p className="text-sm text-muted-foreground">{c.email}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">{c.nb_ventes}</p>
                      <p className="text-xs text-muted-foreground">Ventes</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{c.ca_mensuel}€</p>
                      <p className="text-xs text-muted-foreground">CA mensuel</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog: Détail Pack Métier */}
      <Dialog open={!!showPackDetail} onOpenChange={() => setShowPackDetail(null)}>
        <DialogContent className="sm:max-w-lg">
          {showPackDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => {
                    const Icon = PACK_ICONS[Object.keys(packsMetiers).find(k => packsMetiers[k] === showPackDetail)] || Package;
                    return <Icon className="w-5 h-5" style={{ color: showPackDetail.couleur }} />;
                  })()}
                  {showPackDetail.nom}
                </DialogTitle>
                <DialogDescription>{showPackDetail.description}</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-sm mb-2">Modules inclus</h4>
                  <div className="flex flex-wrap gap-1">
                    {showPackDetail.modules_inclus?.map(m => (
                      <Badge key={m} variant="outline">{m}</Badge>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-sm mb-2">Registres obligatoires</h4>
                  <div className="space-y-1">
                    {showPackDetail.registres_obligatoires?.map(r => (
                      <div key={r.code} className="flex items-center gap-2 text-sm">
                        <Check className="w-3 h-3 text-green-600" />
                        <span>{r.nom}</span>
                        {r.obligatoire && <Badge className="text-xs bg-red-100 text-red-800">Obligatoire</Badge>}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-sm mb-2">Agents IA spécialisés</h4>
                  <div className="flex flex-wrap gap-1">
                    {showPackDetail.agents_ia?.map(a => (
                      <Badge key={a} className="bg-purple-100 text-purple-800">{a}</Badge>
                    ))}
                  </div>
                </div>
                
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Supplément mensuel</p>
                  <p className="text-2xl font-bold text-green-600">+{showPackDetail.prix_supplementaire}€/mois</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmation création */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-6 h-6" />
              Prospect créé avec succès !
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p>
              Le prospect <strong>{config.nom_entreprise}</strong> a été ajouté au pipeline commercial.
            </p>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-muted-foreground">Prix mensuel proposé</p>
              <p className="text-2xl font-bold text-green-600">{prixAjuste || devis?.total_mensuel}€/mois</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowConfirmation(false);
              setConfig({
                nom_entreprise: '', contact_nom: '', contact_prenom: '',
                contact_email: '', contact_telephone: '', ville: '',
                activites: [], surface_ha: 50, nb_workspaces: 1, nb_collaborateurs: 1,
                options: [], demande_speciale: '', code_parrainage: '',
                type_paiement: 'mensuel', commercial_id: ''
              });
              setDevis(null);
            }}>
              Nouveau prospect
            </Button>
            <Button onClick={() => {
              setShowConfirmation(false);
              setActiveTab('prospects');
            }}>
              Voir le pipeline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfigurateurProspectPage;
